import React, { PureComponent } from 'react';
import { compose } from 'redux';
import { withNamespaces } from 'react-i18next';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { Card } from 'reactstrap';
import Checkbox from 'components/forms/Checkbox';
import NoResults from 'components/NoResults';
import { capitalize } from 'lodash';
import { TOGGLE_ANALYTICS_CHART_LEGEND } from 'constants/analytics';
import withResponsive from 'components/Layout/withResponsive';
import {
  disabledText, blueberry, frenchPink,
  caribbeanGreen, mellowApricot, secondary300, transparent,
} from 'themes/colors';

import {
  BarChart, ResponsiveContainer, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { MIN_DESKTOP_WIDTH } from 'constants/misc';
import { analyticsTrack } from '../../utils/analytics';

const styles = theme => ({
  chartTitle: {
    color: theme.palette.secondary.text,
    fontSize: '1rem',
    fontWeight: 600,
    marginTop: '0.875rem',
    marginBottom: '1rem',
    marginLeft: '2.25rem',
  },
  barChartWrapper: {
    '& .recharts-legend-wrapper': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      [`@media screen and (max-width: ${MIN_DESKTOP_WIDTH}px)`]: {
        justifyContent: 'start',
      },
    },
  },
  legendsWrapper: {
    display: 'flex',
    justifyContent: 'center',
    listStyle: 'none',
    [`@media screen and (max-width: ${MIN_DESKTOP_WIDTH}px)`]: {
      flexDirection: 'column',
    },
  },
  legend: {
    margin: '0 1.094rem',
    fontSize: '0.875rem',
    [`@media screen and (max-width: ${MIN_DESKTOP_WIDTH}px)`]: {
      margin: '0 0',
    },
  },
  tooltipWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    listStyle: 'none',
    backgroundColor: 'white',
    border: '1px solid rgba(0,0,0,.3)',
    padding: '.5rem',
  },
  tooltip: {
    fontSize: '.95rem',
    margin: '.5rem',
  },
});

const defaultDataKeys = [
  { dataKey: 'delivered', color: blueberry },
  { dataKey: 'opened', color: frenchPink },
  { dataKey: 'replied', color: caribbeanGreen },
  { dataKey: 'interested', color: mellowApricot },
];

const defaultCheckboxColors = {
  delivered: 'blue',
  opened: 'pink',
  replied: 'green',
  interested: 'yellow',
};

class ReportsChart extends PureComponent {
  static propTypes = {
    classes: PropTypes.element.isRequired,
    className: PropTypes.string,
    loading: PropTypes.bool.isRequired,
    dataSource: PropTypes.arrayOf(PropTypes.shape({
      date: PropTypes.string,
      opened: PropTypes.number,
      interested: PropTypes.number,
      expanded_interested: PropTypes.string,
    })),
    dataKeys: PropTypes.arrayOf(PropTypes.shape({
      dataKey: PropTypes.string,
      color: PropTypes.string,
    })),
    checkboxColors: PropTypes.shape,
    payload: PropTypes.arrayOf(PropTypes.shape).isRequired,
    isMobile: PropTypes.bool,
  };

  static defaultProps = {
    className: '',
    dataSource: [],
    dataKeys: defaultDataKeys,
    checkboxColors: defaultCheckboxColors,
    isMobile: false,
  };

  constructor(props) {
    super(props);
    const initialState = {};
    props.dataKeys.forEach(({ dataKey }) => {
      initialState[`show${capitalize(dataKey)}`] = true;
    });
    this.state = initialState;
  }

  onLegendChange = legend => (event) => {
    analyticsTrack(
      TOGGLE_ANALYTICS_CHART_LEGEND,
      { legend: capitalize(legend), checked: event.target.checked },
      true,
    );
    const showLegend = `show${capitalize(legend)}`;
    this.setState({ [showLegend]: event.target.checked });
  }

  getDataSource = () => {
    /* eslint-disable no-param-reassign */
    if (this.props.dataSource && this.props.dataSource.length > 0) {
      return this.props.dataSource.map(obj => ({ ...obj })).map((obj) => {
        Object.keys(obj).forEach((key) => {
          if (!this.state[`show${capitalize(key)}`] && key !== 'name') {
            delete obj[key];
          }
        });
        return { ...obj };
      });
    }
    return [];
  }

  renderLegend = classes => (props) => {
    const { payload } = props;
    return (
      <ul className={classes.legendsWrapper}>
        {
          payload.map(entry => (
            <li className={classes.legend} key={entry.value}>
              <Checkbox
                color={this.props.checkboxColors[entry.value]}
                checked={this.state[`show${capitalize(entry.value)}`]}
                onChange={this.onLegendChange(entry.value)}
              />
              {`${entry.value === 'expandedInterest' ? 'Expanded Interest' :
                capitalize(entry.value).replace('_', ' ')}`}
            </li>
          ))
        }
      </ul>
    );
  }

  renderTooltip = classes => ({ payload }) => (
    <ul className={classes.tooltipWrapper}>
      {payload.length &&
        <li className={classes.tooltip}>
          {payload[0].payload.name}
        </li>}
      {
        payload.map(entry => (
          <li
            className={classes.tooltip}
            key={entry.dataKey}
            style={{
              color: entry.color,
            }}
          >
            {`${entry.dataKey === 'expandedInterest' ? 'Expanded Interest' :
              capitalize(entry.dataKey).replace('_', ' ')}`}: {entry.value}
          </li>
        ))
      }
    </ul>);

  render() {
    const {
      classes,
      isMobile,
      dataKeys,
    } = this.props;

    const { dataSource } = this.props;

    return (
      <div>
        {!this.props.loading && dataSource.length ?
          <Card className={this.props.className} style={{ height: 516 }}>
            <h3 className={classes.chartTitle}>Campaign performance over time</h3>
            <ResponsiveContainer>
              <BarChart
                className={classes.barChartWrapper}
                data={this.getDataSource()}
                margin={{
                  top: 20, right: 30, left: 20, bottom: 5,
                }}
              >
                <CartesianGrid vertical={false} />
                <XAxis dataKey="name" tickLine={false} tick={{ fill: disabledText, fontSize: 12 }} dy={15} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: disabledText, fontSize: 12 }} dx={-10} />
                <Tooltip content={this.renderTooltip(classes)} />
                <Legend content={this.renderLegend(classes)} verticalAlign="bottom" height={isMobile ? 220 : 64} />
                {dataKeys.map(bar => (
                  <Bar
                    dataKey={bar.dataKey}
                    minPointSize={8}
                    fill={this.state[`show${capitalize(bar.dataKey)}`] ? bar.color : transparent}
                    barSize={8}
                    radius={[3, 3, 0, 0]}
                  >
                    {
                      dataSource.map((entry) => {
                        // eslint-disable-next-line no-nested-ternary
                        const color = entry[bar.dataKey] > 0
                          ? this.state[`show${capitalize(bar.dataKey)}`]
                            ? bar.color
                            : transparent
                          : this.state[`show${capitalize(bar.dataKey)}`]
                            ? secondary300
                            : transparent;
                        return <Cell fill={color} />;
                      })
                    }
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Card>
          :
          <NoResults
            height="18.75rem"
            className="mb-3"
            loading={this.props.loading}
            results={this.props.dataSource}
          />
        }
      </div>
    );
  }
}

export default compose(
  withResponsive,
  withStyles(styles, { withTheme: true }),
  withNamespaces('common'),
)(ReportsChart);
